import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, CheckCircle2, Circle, Flag } from "lucide-react";
import { format, differenceInDays, addDays, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";

export default function GanttChart({ project, phases, tasks, milestones }) {
  // Calculate date range
  const allDates = [
    project.start_date,
    project.end_date,
    ...phases.map(p => p.created_date),
    ...tasks.map(t => t.due_date).filter(Boolean),
    ...milestones.map(m => m.due_date).filter(Boolean),
  ].filter(Boolean).map(d => new Date(d));

  if (allDates.length === 0) {
    return (
      <Card className="p-6 bg-white border-0 shadow-sm">
        <p className="text-sm text-slate-500 text-center">Ingen datoer satt for dette prosjektet</p>
      </Card>
    );
  }

  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));
  const startDate = startOfMonth(minDate);
  const endDate = endOfMonth(maxDate);
  const totalDays = differenceInDays(endDate, startDate);
  const monthsRange = eachDayOfInterval({ start: startDate, end: endDate })
    .filter(date => date.getDate() === 1);

  const getBarPosition = (start, end) => {
    const itemStart = start ? new Date(start) : startDate;
    const itemEnd = end ? new Date(end) : itemStart;
    const left = (differenceInDays(itemStart, startDate) / totalDays) * 100;
    const width = Math.max((differenceInDays(itemEnd, itemStart) / totalDays) * 100, 2);
    return { left: `${left}%`, width: `${width}%` };
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm overflow-x-auto">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-zinc-900" />
        Gantt-diagram
      </h3>

      {/* Timeline Header */}
      <div className="mb-4">
        <div className="flex border-b border-slate-200 pb-2">
          {monthsRange.map((date, i) => (
            <div key={i} className="flex-1 text-xs text-slate-600 font-medium">
              {format(date, "MMM yyyy", { locale: nb })}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3 min-w-[600px]">
        {/* Project Timeline */}
        {project.start_date && project.end_date && (
          <div className="relative">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-48 flex-shrink-0">
                <p className="text-sm font-medium text-slate-900 truncate">{project.name}</p>
                <p className="text-xs text-slate-500">Prosjekt</p>
              </div>
            </div>
            <div className="relative h-8 bg-slate-50 rounded">
              <div
                className="absolute h-full bg-zinc-900 rounded flex items-center px-2"
                style={getBarPosition(project.start_date, project.end_date)}
              >
                <span className="text-xs text-white font-medium truncate">
                  {format(new Date(project.start_date), "dd.MM")} - {format(new Date(project.end_date), "dd.MM")}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Milestones */}
        {milestones.map((milestone) => (
          <div key={milestone.id} className="relative">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-48 flex-shrink-0">
                <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-1">
                  <Flag className="w-3 h-3 text-amber-600" />
                  {milestone.name}
                </p>
                <p className="text-xs text-slate-500">Milepæl</p>
              </div>
            </div>
            <div className="relative h-8 bg-slate-50 rounded">
              <div
                className="absolute h-full flex items-center"
                style={{ left: getBarPosition(milestone.due_date, milestone.due_date).left }}
              >
                <div className={cn(
                  "w-4 h-8 rounded",
                  milestone.status === "completed" ? "bg-emerald-500" : "bg-amber-500"
                )}>
                  {milestone.status === "completed" && (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Phases */}
        {phases.map((phase) => {
          const phaseTasks = tasks.filter(t => t.phase_id === phase.id && t.due_date);
          if (phaseTasks.length === 0) return null;
          
          const phaseStart = new Date(Math.min(...phaseTasks.map(t => new Date(t.due_date))));
          const phaseEnd = new Date(Math.max(...phaseTasks.map(t => new Date(t.due_date))));

          return (
            <div key={phase.id} className="relative">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-48 flex-shrink-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{phase.name}</p>
                  <p className="text-xs text-slate-500">Fase</p>
                </div>
              </div>
              <div className="relative h-8 bg-slate-50 rounded">
                <div
                  className="absolute h-full bg-blue-400 rounded"
                  style={getBarPosition(phaseStart, phaseEnd)}
                />
              </div>
            </div>
          );
        })}

        {/* Important Tasks */}
        {tasks.filter(t => t.due_date && (t.priority === "high" || t.priority === "urgent")).slice(0, 10).map((task) => (
          <div key={task.id} className="relative">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-48 flex-shrink-0">
                <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                <p className="text-xs text-slate-500">
                  Oppgave • {task.priority === "urgent" ? "Haster" : "Høy"}
                </p>
              </div>
            </div>
            <div className="relative h-8 bg-slate-50 rounded">
              <div
                className={cn(
                  "absolute h-full rounded",
                  task.status === "completed" ? "bg-emerald-400" : 
                  task.priority === "urgent" ? "bg-rose-400" : "bg-amber-400"
                )}
                style={getBarPosition(task.due_date, task.due_date)}
              >
                <div className="w-2 h-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}